import React, {useEffect, useRef, useState} from 'react';
import {
    StaticDatePicker,
    PickersDay,
} from '@mui/x-date-pickers';
import {
    TextField,
    Grid,
    MenuItem,
    Select,
    ButtonGroup,
    Button,
    Typography,
} from '@mui/material';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {
    format,
    isSameDay,
    startOfMonth,
    endOfMonth,
    addDays,
    isAfter,
    parseISO
} from 'date-fns';
import {requestApi} from '../../../utils/utils';
import {styled} from '@mui/material/styles';

const AvailableDay = styled(PickersDay)(({theme}) => ({
    backgroundColor: '#2e7d32',
    color: 'white',
    borderRadius: '50%',
    '&:hover': {
        backgroundColor: '#1b5e20',
    },
}));

export default function AvailabilityCalendar({activity, handlerActivity, inquiryAction, maxPax, removeActivity, fixPax}) {
    const initialDate = new Date();
    const [value, setValue] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableOptions, setAvailableOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState('');
    const initialQt = fixPax > 0 ? fixPax : 0;
    const initialLimit = maxPax > 0 ? maxPax : 0;
    const [quantity, setQuantity] = useState(initialQt);
    const [limit, setLimit] = useState(initialLimit);
    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;

        const startDate = format(startOfMonth(new Date()), 'yyyy-MM-dd');
        const endDate = format(endOfMonth(new Date()), 'yyyy-MM-dd');
        const {company, id: activityId} = activity;

        getAvailability({activityId, company, startDate, endDate}).catch((err) => {
            console.error('Error loading availability:', err);
        });

        return () => {
            isMounted.current = false;
        };
    }, []);

    const getAvailability = async (params) => {
        await requestApi('/get-activities/availability', 'GET', params, (res) => {
            if (!isMounted.current) return;

            const availability = {};
            const availabilityData = res.data.availabilities;
            const dates = Object.keys(availabilityData).map((item) => {
                const date = availabilityData[item].date.split('T')[0];
                if (!availability.hasOwnProperty(date))
                    availability[date] = [{[item]: availabilityData[item]}];
                else availability[date].push({[item]: availabilityData[item]});
                return date;
            });

            setAvailableDates(dates);
            setAvailableOptions(availability);
        });
    };

    const hasAvailability = (date) => {
        const dayAfterTomorrow = addDays(new Date(), 1);
        return availableDates.some((available) =>
            isSameDay(date, new parseISO(available)) &&
            isAfter(date, dayAfterTomorrow)
        );
    };

    const shouldShowAsAvailable = (date) => {
        const dayAfterTomorrow = addDays(new Date(), 1);
        return hasAvailability(date) && isAfter(date, dayAfterTomorrow);
    };

    const handleSelectChange = (event) => {
        const index = event.target.value;
        const dateStr = format(value, 'yyyy-MM-dd');
        const selected = availableOptions[dateStr][index];
        const key = Object.keys(selected)[0];
        const data = selected[key];
        setSelectedOption(index);
        setLimit(data.capacity);

        if (initialQt > 0 && data.capacity < initialQt) {
            setQuantity(initialQt);
            setLimit(initialLimit);
        } else {
            setQuantity(initialQt);
            handlerActivity({id: key, pax: initialQt, data: selected[key]});
        }
    };

    const handleIncrement = () => {
        if (quantity < limit && quantity < maxPax) {
            const newQty = quantity + 1;
            setQuantity(newQty);
            updatePax(newQty);
        }
    };

    const handleDecrement = () => {
        if (quantity > 1) {
            const newQty = quantity - 1;
            setQuantity(newQty);
            updatePax(newQty);
        }
    };

    const isPastDate = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date <= today;
    };

    const updatePax = (newQty) => {
        const dateStr = format(value, 'yyyy-MM-dd');
        const selected = availableOptions[dateStr][selectedOption];
        const key = Object.keys(selected)[0];
        handlerActivity({id: key, pax: newQty, data: selected[key]});
    };

    const removePreviousActivity = () => {
        if (!value || value === initialDate) return;
        const dateStr = format(value, 'yyyy-MM-dd');
        const options = availableOptions[dateStr];
        console.log(options);
        if (!options || options.length === 0) return;
        if(selectedOption==='') return;
        const selected = options[selectedOption];
        console.log(selected);
        if (!selected || Object.keys(selected).length === 0) return;
        const key = Object.keys(selected)[0];
        removeActivity({id: key});
        setSelectedOption('');
        setQuantity(initialQt);
        setLimit(initialLimit);
    };

    const allOptionsInvalid = () => {
        const dateStr = format(value, 'yyyy-MM-dd');
        const options = availableOptions[dateStr] || [];
        return options.length > 0 && options.every((option) => {
            const key = Object.keys(option)[0];
            const data = option[key];
            return initialQt > 0 && data.capacity < initialQt;
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container>
                <Grid item sm={12} md={6} xs={12}>
                    <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        value={value}
                        onChange={(newDate) => {
                            removePreviousActivity();
                            setValue(newDate);

                            const dateStr = format(newDate, 'yyyy-MM-dd');
                            const options = availableOptions[dateStr];

                            if (!options || options.length === 0) {
                                setSelectedOption('');
                                setQuantity(initialQt);
                                setLimit(initialLimit);
                            }
                        }}
                        onMonthChange={(newMonth) => {
                            const startDate = format(startOfMonth(newMonth), 'yyyy-MM-dd');
                            const endDate = format(endOfMonth(newMonth), 'yyyy-MM-dd');
                            const {company, id: activityId} = activity;
                            getAvailability({activityId, company, startDate, endDate});
                        }}
                        renderInput={(params) => <TextField {...params} />}
                        shouldDisableDate={(date) => !shouldShowAsAvailable(date)}
                        slots={{
                            day: (props) => {
                                const { day } = props;
                                if (shouldShowAsAvailable(day)) {
                                    return <AvailableDay {...props} />;
                                }
                                return (
                                    !isPastDate(day) && day !== initialDate ?
                                        <PickersDay
                                            {...props}
                                            disabled={false}
                                            style={{opacity: 0.3, background: '#777', color: '#fff'}}
                                        />
                                        :
                                        <PickersDay
                                            {...props}
                                            disabled={true}
                                        />
                                );
                            }
                        }}
                    />
                </Grid>

                {value !== null && value !== initialDate && !hasAvailability(value) && !isPastDate(value) && (
                    <Grid item sm={12} md={6} xs={12} container justifyContent="center" alignItems="center">
                        <Typography variant="h4" align="left" style={{padding: '10px 12px 10px'}}>
                            While we don't have a schedule for this date, click here to ask us to schedule a special
                            departure
                        </Typography>
                        <Button
                            color="primary"
                            variant="contained"
                            fullWidth
                            onClick={() => inquiryAction(format(value, 'yyyy-MM-dd'))}
                        >
                            Inquiry
                        </Button>
                    </Grid>
                )}

                {hasAvailability(value) &&
                    Array.isArray(availableOptions[format(value, 'yyyy-MM-dd')]) &&
                    availableOptions[format(value, 'yyyy-MM-dd')].length > 0 && (
                    <Grid item sm={12} md={6} xs={12} container spacing={2} alignItems="center" justifyContent="center">
                        <Grid item md={12}>
                            <Select
                                fullWidth
                                value={selectedOption}
                                displayEmpty
                                onChange={handleSelectChange}
                            >
                                <MenuItem disabled value="">
                                    Select Time
                                </MenuItem>
                                {availableOptions[format(value, 'yyyy-MM-dd')].map((option, index) => {
                                    const key = Object.keys(option)[0];
                                    const item = option[key];
                                    const time = format(parseISO(item.date), 'hh:mm a');
                                    const isDisabled = initialQt > 0 && item.capacity < initialQt;

                                    return (
                                        <MenuItem key={key} value={index} disabled={isDisabled}>
                                            {time} — {item.customer_prototypes.note}
                                            {isDisabled && ' (Unavailable for selected quantity)'}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </Grid>

                        {allOptionsInvalid() && (
                            <Grid item md={12}>
                                <Typography variant="body1" align="center" style={{paddingTop: '10px'}}>
                                    None of the available times can accommodate your group of {initialQt} guest{initialQt > 1 ? 's' : ''}.
                                    Please click below to request a custom departure.
                                </Typography>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    fullWidth
                                    onClick={() => inquiryAction(format(value, 'yyyy-MM-dd'))}
                                >
                                    Inquiry
                                </Button>
                            </Grid>
                        )}
                        {selectedOption !== '' && (
                            <Grid item md={6} container justifyContent="center" alignItems="center">
                                {initialQt === 0 ? (
                                    <ButtonGroup
                                        variant="text"
                                        size="small"
                                        aria-label="small outlined button group"
                                        className={'numBerths'}
                                    >
                                        {quantity > 0 && (
                                            <Button color="secondary" onClick={handleDecrement}>
                                                -
                                            </Button>
                                        )}
                                        {quantity > 0 && (
                                            <Button color="secondary" disabled>
                                                {quantity}
                                            </Button>
                                        )}
                                        <Button
                                            color="secondary"
                                            onClick={handleIncrement}
                                            disabled={quantity >= limit || quantity >= maxPax}
                                        >
                                            +
                                        </Button>
                                        <span style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>Guests</span>
                                    </ButtonGroup>
                                ) : (
                                    <Typography variant="body1">
                                        Guests: {quantity}
                                    </Typography>
                                )}
                            </Grid>
                        )}
                    </Grid>
                )}
            </Grid>
        </LocalizationProvider>
    );
}
